import type { BsbDirectoryDetail, BsbDirectoryRow, BsbDirectoryStatus } from './bsbDirectoryApiTypes';

/** Presentation helpers for the BSB Directory. Kept free of Mango imports so they stay unit-testable. */

export const BSB_DIRECTORY_PAGE_SIZE = 25;

export const statusLabels: Record<BsbDirectoryStatus, string> = {
  active: 'Active',
  merged: 'Merged',
  withdrawn: 'Withdrawn',
};

/** Merged is a caution rather than a failure: the BSB still resolves, via a replacement. */
export const statusTones: Record<BsbDirectoryStatus, 'positive' | 'caution' | 'critical'> = {
  active: 'positive',
  merged: 'caution',
  withdrawn: 'critical',
};

export const statusOptions = [
  { label: 'All statuses', value: '' },
  ...(Object.keys(statusLabels) as BsbDirectoryStatus[]).map((value) => ({ label: statusLabels[value], value })),
];

export const electronicOptions = [
  { label: 'All', value: '' },
  { label: 'Electronic', value: 'true' },
  { label: 'Non-electronic', value: 'false' },
];

/** The Australian states and territories the AusPayNet directory uses. */
export const stateOptions = [
  { label: 'All states', value: '' },
  ...['ACT', 'NSW', 'NT', 'QLD', 'SA', 'TAS', 'VIC', 'WA'].map((value) => ({ label: value, value })),
];

export const locationLabel = (row: Pick<BsbDirectoryRow, 'city' | 'state'>) =>
  [row.city, row.state].filter(Boolean).join(' ') || '—';

export const displayValue = (value?: string | null) => (value == null || value === '' ? '—' : value);

export const addressLines = (detail: BsbDirectoryDetail) =>
  [detail.address, [detail.city, detail.state, detail.postcode].filter(Boolean).join(' ')].filter(Boolean) as string[];

/**
 * Operator-friendly provenance. Formats the last successful directory refresh and
 * whether it is inside the configured cadence. Never surfaces ingestion identifiers.
 */
export const provenanceLabel = (detail: BsbDirectoryDetail) => {
  const { refreshedAt, isStale } = detail.provenance;
  if (!refreshedAt) return { refreshed: 'Not available', status: 'Stale' as const, isStale: true };
  const parsed = new Date(refreshedAt);
  const refreshed = Number.isNaN(parsed.getTime())
    ? 'Not available'
    : parsed.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
  return { refreshed, status: isStale ? ('Stale' as const) : ('Current' as const), isStale };
};
